import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { newsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { formatDateTime } from '../../utils/dateUtils';
import { ensureHttps } from '../../utils/urlUtils';
import './NewsDetailPage.scss';

const NewsDetailPage = () => {
  const { slug } = useParams();
  const { user, isAuthenticated } = useAuth();
  const { t, getLocalizedField, language } = useSettings();
  const [news, setNews] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    fetchNews();
    fetchComments();
  }, [slug]);

  const fetchNews = async () => {
    try {
      const response = await newsAPI.getBySlug(slug);
      setNews(response.data);
      setIsLiked(response.data.is_liked);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Новость не найдена');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await newsAPI.getComments(slug);
      setComments(response.data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      alert('Войдите, чтобы лайкнуть новость');
      return;
    }

    try {
      if (isLiked) {
        await newsAPI.unlike(slug);
        setIsLiked(false);
        setNews({ ...news, likes_count: news.likes_count - 1 });
      } else {
        await newsAPI.like(slug);
        setIsLiked(true);
        setNews({ ...news, likes_count: news.likes_count + 1 });
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await newsAPI.createComment({
        news: news.id,
        text: newComment
      });
      setNewComment('');
      fetchComments();
    } catch (err) {
      console.error('Error posting comment:', err);
      alert('Ошибка при отправке комментария');
    }
  };

  if (loading) {
    return (
      <div className="news-detail-page">
        <div className="container">
          <div className="loading">{t('newsLoading')}</div>
        </div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="news-detail-page">
        <div className="container">
          <div className="error">{error}</div>
          <Link to="/news" className="back-button">{t('backToNews')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="news-detail-page">
      <div className="container">
        <Link to="/news" className="back-button">{t('allNews')}</Link>

        <article className="news-article">
          <header className="article-header">
            {news.category && (
              <span className="article-category">{getLocalizedField(news.category, 'name')}</span>
            )}
            <h1 className="article-title">{getLocalizedField(news, 'title')}</h1>
            <div className="article-meta">
              <span>{t('author')}: {news.author_name || t('editorial')}</span>
              <span>•</span>
              <span>{formatDateTime(news.published_at || news.created_at, language)}</span>
            </div>
          </header>

          {news.cover_image && (
            <div className="article-image">
              <img src={ensureHttps(news.cover_image)} alt={getLocalizedField(news, 'title')} />
            </div>
          )}

          <div className="article-content">
            <p className="article-summary">{getLocalizedField(news, 'summary')}</p>
            <div className="article-text">{getLocalizedField(news, 'content')}</div>

            {news.media_files && news.media_files.length > 0 && (
              <div className="article-gallery">
                <h3>{t('gallery')}</h3>
                <div className="gallery-grid">
                  {news.media_files.map((file) => (
                    <div key={file.id} className="gallery-item">
                      {file.media_type === 'video' ? (
                        <video controls src={ensureHttps(file.file)} className="gallery-media" />
                      ) : (
                        <img 
                          src={ensureHttps(file.file)} 
                          alt={file.caption || getLocalizedField(news, 'title')} 
                          className="gallery-media"
                          onClick={() => window.open(ensureHttps(file.file), '_blank')}
                        />
                      )}
                      {file.caption && <p className="media-caption">{file.caption}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="article-actions">
            <button
              className={`like-button ${isLiked ? 'liked' : ''}`}
              onClick={handleLike}
            >
              ❤️ {news.likes_count}
            </button>
            <span className="views-count">{t('viewsCount')} {news.views_count}</span>
          </div>

          {news.allow_comments && (
            <div className="comments-section">
              <h2 className="section-title">
                Комментарии ({news.comments_count})
              </h2>

              {isAuthenticated ? (
                <form className="comment-form" onSubmit={handleCommentSubmit}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={t('writeComment')}
                    rows="3"
                    required
                  />
                  <button type="submit" className="submit-button">
                    {t('send')}
                  </button>
                </form>
              ) : (
                <p className="auth-prompt">
                  {t('loginToComment')}
                </p>
              )}

              <div className="comments-list">
                {comments.map((comment) => (
                  <div key={comment.id} className="comment">
                    <div className="comment-header">
                      <span className="comment-author">
                        {comment.user_name || comment.user_email}
                      </span>
                      <span className="comment-date">
                        {formatDateTime(comment.created_at, language)}
                      </span>
                    </div>
                    <p className="comment-text">{comment.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default NewsDetailPage;
